import { AsyncPipe } from '@angular/common';
import { Component, inject, input, signal } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCheck, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Observable } from 'rxjs';
import { toNumber } from 'src/app/@common/util/to-number';
import { PlaylistService } from 'src/app/@domain/playlist/application/playlist.service';
import { onFirstClick } from '../../../../@common/DOM/close-on-first-click.function';
import { Modal } from '../../../../@domain/modal/modal.enum';
import { ModalService } from '../../../../@domain/modal/modal.service';
import { PlaylistInfo } from '../../../../@generated';
import { AppRoute } from '../../../../routing/types/app-route';
import { CreatePlaylistModalComponent } from '../../modals/create-playlist-modal/create-playlist-modal.component';

@Component({
    selector: 'app-playlist-dropdown',
    imports: [
        FaIconComponent,
        AsyncPipe,
        CreatePlaylistModalComponent
    ],
    styles: `
        @use "variables" as *;

        :host {
            position: relative;
        }

        .btn-trans {
            padding: 8px 16px;

            @media screen and (max-width: 500px) {
                padding: 6px 12px;
            }
        }

        .dropdown {
            .icon {
                color: $grey;

                fa-icon {
                    padding-left: 0.4rem;
                }

                &:hover {
                    color: $grey-dark;
                }
            }
        }

        .menu {
            max-width: fit-content;
            min-width: 200px;
            padding: 0.2rem;
            background-color: $white;
            visibility: hidden;
            position: absolute;
            z-index: 1;
            bottom: 0;
            left: 0;
            transform: translateY(calc(100% - 17px));
            max-height: 250px;
            overflow-y: auto;
            box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);

            &.open {
                visibility: visible;
                transition: all 0.2s ease;
            }

            button {
                width: 100%;
                min-width: fit-content;
                font-weight: 600;

                fa-icon {
                    padding-right: 0.4rem;
                }
            }
        }

        .dropdown-item {
            display: flex;
            padding: 0.5rem;
            cursor: pointer;
            font-size: 1rem;
            transition: background-color 0.1s, font-weight 0.1s;

            &.added {
                cursor: default;
                font-weight: 500;
                pointer-events: none;
                color: black
            }

            &:hover,
            &.active {
                background-color: #f3f4f6;
                font-weight: 500;
            }
        }
    `,
    template: `
        <button class="btn btn-trans">
            <div class="dropdown position-left">
                <div class="icon" (click)="toggleDropdown()">
                    Playlist
                    <fa-icon [icon]="faPlus"/>
                </div>
            </div>
        </button>
        <div class="menu" [class.open]="dropdownOpen()">
            <button class="btn btn-primary border-radius-small" (click)="addNewPlaylist()">
                <fa-icon [icon]="faPlus"/>
                New playlist
            </button>
            @for (playlist of playlists$ | async; track playlist.id) {
                @let isAdded = isInPlaylist(playlist, videoId()!);

                <fa-icon [icon]="faCheck" [hidden]="!isAdded"/>

                <div class="dropdown-item" [class.added]="isAdded" (click)="addToPlaylist(playlist.id)">
                    {{ playlist.title }}
                </div>
            }
        </div>

        <app-create-playlist-modal/>
    `
})
export class PlaylistDropdownComponent {
    private playlistService = inject(PlaylistService);
    private modalService = inject(ModalService);

    public videoId = input.required({ transform: toNumber() });
    public playlists$: Observable<PlaylistInfo[]> = this.playlistService.getUsersPlaylists();
    public dropdownOpen = signal(false);
    public faPlus = faPlus;
    public AppRoutes = AppRoute;
    public faCheck = faCheck;

    public toggleDropdown() {
        this.dropdownOpen.set(!this.dropdownOpen());

        if (this.dropdownOpen()) {
            onFirstClick(() => this.dropdownOpen.set(false));
        }
    }

    public addToPlaylist(playlistId: number) {
        return this.playlistService.addVideoToPlaylist(playlistId, this.videoId()!);
    }

    public addNewPlaylist(): void {
        this.modalService.open(Modal.CreatePlaylist);
    }

    public isInPlaylist(playlist: PlaylistInfo, videoId: number): boolean {
        return playlist.videos.some(id => id === videoId);
    }
}
